import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/features/auth/AuthProvider";
import { useAdminRole } from "@/features/admin/hooks/useAdminRole";

/**
 * Route guard for admin-only pages.
 *
 * Renders nothing while either auth or the admin-role query is loading.
 * Redirects unauthenticated users to /auth and non-admins to /dashboard.
 * Defense-in-depth: even if `loading` finishes with `isAdmin === false`,
 * the Navigate fallback ensures we never render admin children.
 */
export const AdminGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useAdminRole();

  if (authLoading || (user && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
